import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { commitMutation, graphql } from 'react-relay';
import { ConnectionHandler } from 'relay-runtime';
import { Analytics, GlobalNotifications, Intent } from '../../Modules';
import nullifyEmptyStrings from '../../../lib/utils/nullifyEmptyStrings';
import Library from '../Sections/Library/LibraryContainer';

export default class ContractLibrary extends React.Component {

  static propTypes = {
    node: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object
  }

  constructor(props) {
    super(props);
    this.copyLibraryLineItemsToContract = this.copyLibraryLineItemsToContract.bind(
      this
    );
    this.createLineItem = this.createLineItem.bind(this);
    this.resetNewLibraryLineItem = this.resetNewLibraryLineItem.bind(this);
    this.newLibraryLineItemChangeHandler = this.newLibraryLineItemChangeHandler.bind(
      this
    );
  }

  componentWillMount() {
    // Initialize new library line item
    this.resetNewLibraryLineItem();
  }

  copyLibraryLineItemsToContract(itemsToCopy) {
    const onError = () => {
      GlobalNotifications.show({
        message: `Unable to add line item${itemsToCopy.length > 1 ? 's' : ''}`,
        intent: Intent.DANGER
      });
    };

    const onCompleted = () => {
      GlobalNotifications.show({
        message: `Successfully added line item${itemsToCopy.length > 1
          ? 's'
          : ''}`,
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('added-line-items-from-library', {
        item_count: itemsToCopy.length
      });
    };

    const mutation = graphql`
      mutation Library_Contract_copyUserLineItemsToContractMutation(
        $input: CopyUserLineItemsToContractInput!
      ) {
        copyUserLineItemsToContract(input: $input) {
          contract {
            contract_line_items {
              edges {
                node {
                  id
                  title
                  description
                  position
                  price_cents
                  price_currency
                  quantity
                }
              }
            }
          }
        }
      }
    `;

    const parentID = this.props.node.id;

    const updater = store => {
      const contractProxy = store.get(this.props.node.id);
      const payload = store.getRootField('copyUserLineItemsToContract');
      const newContractLineItems = payload
        .getLinkedRecord('contract')
        .getLinkedRecord('contract_line_items');
      const conn = ConnectionHandler.getConnection(
        contractProxy,
        'ContractContainer_contract_line_items'
      );
      conn.copyFieldsFrom(newContractLineItems);
    };

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          contract_id: parentID,
          user_line_item_ids: itemsToCopy
        }
      },
      updater,
      onCompleted,
      onError
    });
  }

  createLineItem(idx, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to add line item',
        intent: Intent.DANGER
      });
    };

    const onCompleted = () => {
      if (successCb) successCb();
      this.resetNewLibraryLineItem();
      GlobalNotifications.show({
        message: 'Successfully added line item',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('created-line-item');
    };

    const mutation = graphql`
      mutation Library_Contract_createContractLineItemMutation(
        $input: CreateContractLineItemInput!
      ) {
        createContractLineItem(input: $input) {
          new_contract_line_item_edge {
            node {
              id
              title
              description
              position
              price_cents
              price_currency
              quantity
            }
          }
          new_user_line_item_edge {
            node {
              id
              title
              description
              price_cents
              price_currency
              quantity
            }
          }
        }
      }
    `;

    const newLineItem = _.pick(
      nullifyEmptyStrings(this.state.newLibraryLineItem),
      'title',
      'description',
      'price_cents',
      'price_currency',
      'quantity'
    );

    newLineItem.quantity = parseInt(newLineItem.quantity, 10);

    const parentID = this.props.node.id;

    const updater = store => {
      // Update contract
      const contractProxy = store.get(this.props.node.id);
      const payload = store.getRootField('createContractLineItem');
      const newContractLineItemEdge = payload.getLinkedRecord('new_contract_line_item_edge');
      const contractLineItemConn = ConnectionHandler.getConnection(
        contractProxy,
        'ContractContainer_contract_line_items'
      );
      ConnectionHandler.insertEdgeAfter(contractLineItemConn, newContractLineItemEdge);

      // Update viewer if adding to library
      if (this.state.newLibraryLineItem.addToLibrary) {
        const viewerProxy = store.get(this.props.viewer.id);
        const newUserLineItemEdge = payload.getLinkedRecord('new_user_line_item_edge');
        const userLineItemConn = ConnectionHandler.getConnection(
          viewerProxy,
          'LibraryContainer_user_line_items'
        );
        ConnectionHandler.insertEdgeAfter(userLineItemConn, newUserLineItemEdge);
      }
    };

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          contract_id: parentID,
          ...newLineItem,
          add_to_library: this.state.newLibraryLineItem.addToLibrary
        }
      },
      updater,
      onCompleted,
      onError
    });
  }

  resetNewLibraryLineItem() {
    this.setState({
      newLibraryLineItem: {
        title: '',
        description: '',
        quantity: 1,
        price_cents: 0,
        price_currency: 'USD',
        addToLibrary: false
      }
    });
  }

  newLibraryLineItemChangeHandler(lineItem) {
    const obj = _.cloneDeep(this.state);
    obj.newLibraryLineItem = lineItem;
    this.setState({ ...obj });
  }

  render() {
    const contractLineItems = this.props.node.contract_line_items.edges;
    return (
      <Library
        initiallyExpanded={contractLineItems.length === 0}
        newLibraryLineItem={this.state.newLibraryLineItem}
        newLibraryLineItemChangeHandler={
          this.newLibraryLineItemChangeHandler
        }
        resetNewLibraryLineItem={this.resetNewLibraryLineItem}
        createLineItem={this.createLineItem}
        copyLibraryLineItemsToDocument={
          this.copyLibraryLineItemsToContract
        }
        viewer={this.props.viewer}
        relay={this.props.relay}
      />
    );
  }
}



// WEBPACK FOOTER //
// ./webpack/components/Document/Contract/Library.js